import { type ButtonProps } from '@tokenterminal/ui/Button/Button.v3'
import { type ButtonV4Props } from '@tokenterminal/ui/Button/Button.v4'
import { assignInlineVars } from '@vanilla-extract/dynamic'
import {
  useRef,
  useState,
  type ReactElement,
  useLayoutEffect,
  type ElementType,
  useMemo,
} from 'react'
import { Box, type BoxProps } from '../../Box'
import { type PopoverProps, usePopover } from '../../Popover'
import { Stack } from '../../Stack'
import { withOverflow } from '../../util.css'
import { Button } from './Button'
import { useButtonProps } from './hooks/useButtonProps'
import { useListNavigation } from './hooks/useListNavigation'
import { useSelectState } from './hooks/useSelectState'
import { Label, type LabelProps, type LabelVariants } from './Label'
import { List } from './List'
import { type Option, type OptionProps } from './Option'
import { Popover } from './Popover'
import { selectBaseCss, widthVar } from './Select.css'
import { SelectContextProvider } from './SelectContext'

type Overrides<T> = {
  Label?: ElementType<LabelProps<T>>
}

type SelectPropsBase<T> = {
  label?: string
  children?: Array<ReactElement<OptionProps<T>, typeof Option<T>>>
  labelVariant?: LabelVariants
  disabled?: boolean
  width?: string
  className?: BoxProps['className']
  componentOverrides?: Overrides<T>
  display?: BoxProps['display']
  clearable?: boolean
} & (
  | {
      selected?: undefined
      defaultSelected?: T
      onChange?: (item: T) => void
    }
  | {
      defaultSelected?: undefined
      selected?: T
      onChange: (item: T) => void
    }
)

export type SelectProps<T> = SelectPropsBase<T> &
  (
    | {
        version?: '3'
        size?: ButtonProps['size']
        variant?: ButtonProps['variant']
      }
    | {
        version: '4'
        size?: ButtonV4Props['size']
        variant?: ButtonV4Props['variant']
      }
  ) &
  Omit<BoxProps, keyof SelectPropsBase<T> | 'size' | 'variant'>

function SelectList<T>({
  children,
  setOpen,
}: {
  children: Array<ReactElement<OptionProps<T>, typeof Option<T>>>
  setOpen: (isOpen: boolean) => void
}) {
  const listRef = useRef<HTMLUListElement>(null)
  const handlers = useListNavigation({
    items: children,
    listRef,
    setOpen,
  })

  useLayoutEffect(() => {
    requestAnimationFrame(() => {
      listRef.current?.focus()
    })
  }, [])

  return (
    <List
      className={withOverflow}
      style={{ maxHeight: 300 }}
      ref={listRef}
      {...handlers}
    >
      {children}
    </List>
  )
}

export function Select<T>({
  defaultSelected,
  selected,
  label,
  onChange,
  width,
  className = selectBaseCss,
  disabled,
  labelVariant = 'value',
  children = [],
  componentOverrides,
  version = '3',
  size,
  variant,
  clearable,
  ...props
}: SelectProps<T>) {
  const [defaultValue] = useState(
    defaultSelected ? new Set([defaultSelected]) : new Set(),
  )
  const memoizedSelected = useMemo(() => {
    return typeof selected === 'undefined' ? undefined : new Set([selected])
  }, [selected])
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { popoverMinWidth } = useSelectState({
    triggerRef: buttonRef,
    minWidth: 200,
  })
  const { isOpen, setOpen, popoverProps, triggerProps } = usePopover({
    triggerRef: buttonRef,
    trigger: 'Select',
  })
  const buttonProps = useButtonProps({
    isOpen,
    setOpen,
    disabled,
  })
  const selectBoxRef = useRef(null)
  const LabelComponent = componentOverrides?.Label ?? Label

  return (
    <SelectContextProvider
      clearable={clearable}
      defaultValue={defaultValue}
      selected={memoizedSelected}
      onChange={(items) => {
        onChange?.(items.values().next().value as T)
        setOpen(false)
      }}
    >
      <Box
        style={
          width
            ? assignInlineVars({
                [widthVar]: width,
              })
            : undefined
        }
        ref={selectBoxRef}
        display="inlineBlock"
        className={className}
        {...props}
      >
        {/* @ts-ignore */}
        <Button
          width="100%"
          {...triggerProps}
          {...buttonProps}
          variant={variant}
          size={size}
          version={version}
        >
          <LabelComponent
            variant={labelVariant}
            items={children}
            size={version === '3' ? '12' : '14'}
          >
            {label}
          </LabelComponent>
        </Button>

        <Popover
          {...(popoverProps as Omit<PopoverProps, 'minWidth' | 'maxHeight'>)}
          minWidth={popoverMinWidth}
          label="Select"
          borderRadius={version === '4' ? '2.5x' : undefined}
        >
          <SelectList setOpen={setOpen}>{children}</SelectList>
        </Popover>

        <Stack
          as="ul"
          visibility="hidden"
          height="0"
          pointerEvents="none"
          overflow="hidden"
        >
          {children}
        </Stack>
      </Box>
    </SelectContextProvider>
  )
}
